import React, { useState, useEffect } from 'react';
import { controlsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Shield, Search, Loader2, FileText } from 'lucide-react';

const ISOControlsPage = () => {
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchControls();
  }, []);

  const fetchControls = async () => {
    try {
      const res = await controlsAPI.getAll();
      setControls(res.data);
    } catch (error) {
      console.error('Error fetching controls:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(controls.map((c) => c.category))];

  const filteredControls = controls.filter((control) => {
    const matchesSearch =
      control.control_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      control.control_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      control.audit_question.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === 'all' || control.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryColor = (category) => {
    const colors = {
      Organizational: 'bg-blue-100 text-blue-800 border-blue-200',
      People: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      Physical: 'bg-amber-100 text-amber-800 border-amber-200',
      Technological: 'bg-purple-100 text-purple-800 border-purple-200',
    };
    return colors[category] || 'bg-slate-100 text-slate-800 border-slate-200';
  };

  const controlsByCategory = categories.slice(1).map((cat) => ({
    category: cat,
    count: controls.filter((c) => c.category === cat).length,
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="iso-controls-page">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="metric-card md:col-span-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="metric-label">Total Controls</p>
              <p className="metric-value text-[#4A5690]">{controls.length}</p>
            </div>
            <Shield size={24} className="text-[#4A5690]" />
          </div>
        </Card>
        {controlsByCategory.map((cat) => (
          <Card key={cat.category} className="metric-card">
            <p className="metric-label">{cat.category}</p>
            <p className="metric-value text-slate-700">{cat.count}</p>
          </Card>
        ))}
      </div>

      {/* Search & Filter */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[250px] relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search controls..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-controls"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className={selectedCategory === cat ? 'bg-[#4A5690]' : ''}
                  data-testid={`filter-${cat}`}
                >
                  {cat === 'all' ? 'All' : cat}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Controls List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Shield size={20} className="text-[#4A5690]" />
            ISO 27001:2022 Controls ({filteredControls.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#4A5690] mx-auto" />
            </div>
          ) : filteredControls.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <FileText size={40} className="mx-auto mb-3 text-slate-300" />
              <p>No controls match your search</p>
            </div>
          ) : (
            <ScrollArea className="h-[600px]">
              <div className="divide-y">
                {filteredControls.map((control) => (
                  <div
                    key={control.id}
                    className="p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`control-row-${control.control_id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <Badge className={`${getCategoryColor(control.category)} border font-mono`}>
                          {control.control_id}
                        </Badge>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-900 mb-1">
                          {control.control_title}
                        </h4>
                        <p className="text-sm text-slate-600 mb-2">{control.audit_question}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span className="font-medium">Evidence:</span>
                          <span>{control.evidence_required}</span>
                        </div>
                      </div>
                      <Badge variant="outline" className="flex-shrink-0">
                        {control.category}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ISOControlsPage;
